import React, { useMemo } from "react";
import { cn } from "../utils/cn";
import { formatCurrency } from "../utils/formatting";
import type { ProjectionTableRow } from "../types/projection";

interface ProjectionCardProps {
  /** All projection rows */
  rows: ProjectionTableRow[];
  /** Currently visible card index */
  currentIndex: number;
  /** Called when navigating to a different card */
  onNavigate: (index: number) => void;
  /** Currency code */
  currency?: string;
}

interface AssetSegment {
  label: string;
  value: number;
  colorClass: string;
}

export const ProjectionCard: React.FC<ProjectionCardProps> = React.memo(
  ({ rows, currentIndex, onNavigate, currency = "CAD" }) => {
    const row = rows[currentIndex];
    const prevRow = currentIndex > 0 ? rows[currentIndex - 1] : null;

    const isFirst = currentIndex === 0;
    const isLast = currentIndex === rows.length - 1;
    const isRetirement =
      row.phase === "retired" &&
      (currentIndex === 0 || rows[currentIndex - 1].phase === "working");

    // Year-over-year change
    const yoyChange = prevRow ? row.netWorth - prevRow.netWorth : null;
    const yoyPercent =
      prevRow && prevRow.netWorth !== 0
        ? ((row.netWorth - prevRow.netWorth) / Math.abs(prevRow.netWorth)) * 100
        : null;

    // Asset segments for stacked bar
    const segments = useMemo<AssetSegment[]>(() => {
      const totalAssets =
        row.liquidAssets + row.appreciableAssets + row.depreciatingAssets;
      if (totalAssets <= 0) return [];

      const segs: AssetSegment[] = [];
      if (row.liquidAssets / totalAssets > 0.05) {
        segs.push({
          label: "Liquid",
          value: row.liquidAssets,
          colorClass: "projection-card-bar-segment--liquid",
        });
      }
      if (row.appreciableAssets / totalAssets > 0.05) {
        segs.push({
          label: "Property",
          value: row.appreciableAssets,
          colorClass: "projection-card-bar-segment--property",
        });
      }
      if (row.depreciatingAssets / totalAssets > 0.05) {
        segs.push({
          label: "Vehicles",
          value: row.depreciatingAssets,
          colorClass: "projection-card-bar-segment--vehicles",
        });
      }
      return segs;
    }, [row.liquidAssets, row.appreciableAssets, row.depreciatingAssets]);

    const totalAssets =
      row.liquidAssets + row.appreciableAssets + row.depreciatingAssets;

    return (
      <div
        className={cn(
          "projection-card",
          isRetirement && "projection-card--retirement",
          row.phase === "retired" && "projection-card--retired"
        )}
      >
        {/* Header */}
        <div className="projection-card-header">
          <span className="projection-card-year">
            {row.year} (Age {row.age})
          </span>
          <span
            className={cn(
              "projection-card-phase-badge",
              row.phase === "working"
                ? "projection-card-phase-badge--working"
                : "projection-card-phase-badge--retired"
            )}
          >
            {row.phase === "working" ? "Working" : "Retired"}
          </span>
        </div>

        {/* Hero: Net Worth */}
        <div className="projection-card-networth">
          <span className="projection-card-networth-label">NET WORTH</span>
          <span className="projection-card-networth-value">
            {formatCurrency(row.netWorth, currency)}
          </span>

          {/* Year-over-year change */}
          <span
            className={cn(
              "projection-card-yoy-change",
              yoyChange !== null && yoyChange > 0
                ? "projection-card-yoy-change--positive"
                : yoyChange !== null && yoyChange < 0
                  ? "projection-card-yoy-change--negative"
                  : "projection-card-yoy-change--neutral"
            )}
          >
            {yoyChange !== null ? (
              <>
                {yoyChange >= 0 ? "+" : ""}
                {formatCurrency(yoyChange, currency)}{" "}
                {yoyChange > 0 ? "↑" : yoyChange < 0 ? "↓" : "→"}
                {yoyPercent !== null && (
                  <span className="projection-card-yoy-percent">
                    {" "}
                    ({yoyPercent >= 0 ? "+" : ""}
                    {yoyPercent.toFixed(1)}%)
                  </span>
                )}
              </>
            ) : (
              "—"
            )}
          </span>
        </div>

        {/* Asset breakdown stacked bar */}
        {segments.length > 1 && (
          <div className="projection-card-bar-container">
            <div className="projection-card-bar">
              {segments.map((seg) => (
                <div
                  key={seg.label}
                  className={cn("projection-card-bar-segment", seg.colorClass)}
                  style={{
                    width: `${(seg.value / totalAssets) * 100}%`,
                  }}
                />
              ))}
            </div>
            <div className="projection-card-bar-legend">
              {segments.map((seg) => (
                <span
                  key={seg.label}
                  className="projection-card-bar-legend-item"
                >
                  <span
                    className={cn(
                      "projection-card-bar-legend-dot",
                      seg.colorClass
                    )}
                  />
                  {seg.label} {Math.round((seg.value / totalAssets) * 100)}%
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Asset detail rows */}
        <div className="projection-card-details">
          <div className="projection-card-detail-row">
            <span>Liquid Assets</span>
            <span>{formatCurrency(row.liquidAssets, currency)}</span>
          </div>
          <div className="projection-card-detail-row">
            <span>Property</span>
            <span>{formatCurrency(row.appreciableAssets, currency)}</span>
          </div>
          {row.depreciatingAssets > 0 && (
            <div className="projection-card-detail-row">
              <span>Vehicles</span>
              <span>{formatCurrency(row.depreciatingAssets, currency)}</span>
            </div>
          )}
          {row.totalDebt > 0 && (
            <div className="projection-card-detail-row projection-card-detail-row--negative">
              <span>Liabilities</span>
              <span>-{formatCurrency(row.totalDebt, currency)}</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="projection-card-nav">
          <button
            type="button"
            className="projection-card-nav-btn"
            disabled={isFirst}
            onClick={() => onNavigate(currentIndex - 1)}
            aria-label="Previous year"
          >
            ← Previous
          </button>

          <span className="projection-card-position">
            {currentIndex + 1} of {rows.length}
          </span>

          <button
            type="button"
            className="projection-card-nav-btn"
            disabled={isLast}
            onClick={() => onNavigate(currentIndex + 1)}
            aria-label="Next year"
          >
            Next →
          </button>
        </div>
      </div>
    );
  }
);

ProjectionCard.displayName = "ProjectionCard";
